import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';

// Section: "User-Friendly Adaptive Features" — a collapsed-by-default
// accordion covering the three headline features that make the Juliet 2
// (and the range) suit riders with a wide range of physical needs. Placed
// on the homepage between the E-Bikes grid and the Adaptive section so it
// reads as an in-depth companion to the Juliet 2 card above.
//
// Diagrams are inline SVG placeholders — indicative line-art in the brand
// palette. Once we commission proper illustrations (or have real
// factory-supplied engineering diagrams from Barry Peng), swap the SVG
// blocks for <img> tags.

const StepThroughDiagram = () => (
  <svg viewBox="0 0 320 180" className="w-full h-40" role="img" aria-label="Diagram of the Juliet 2 low step-through frame geometry">
    {/* Rear wheel */}
    <circle cx="70" cy="130" r="28" fill="none" stroke="currentColor" strokeWidth="2" />
    <circle cx="70" cy="130" r="3" fill="currentColor" />
    {/* Front wheel */}
    <circle cx="250" cy="130" r="28" fill="none" stroke="currentColor" strokeWidth="2" />
    <circle cx="250" cy="130" r="3" fill="currentColor" />
    {/* Low arched top tube — highlighted in gold to emphasise the feature */}
    <path d="M 90 130 Q 160 108 210 130" fill="none" stroke="#C5B358" strokeWidth="3" />
    {/* Seat tube */}
    <line x1="90" y1="130" x2="115" y2="70" stroke="currentColor" strokeWidth="2" />
    <path d="M 100 68 L 130 68" stroke="currentColor" strokeWidth="4" strokeLinecap="round" />
    {/* Head tube + bar */}
    <line x1="210" y1="130" x2="215" y2="80" stroke="currentColor" strokeWidth="2" />
    <path d="M 200 78 L 230 78" stroke="currentColor" strokeWidth="4" strokeLinecap="round" />
    {/* Arrow showing step-over path */}
    <path d="M 155 60 Q 165 90 160 108" fill="none" stroke="#C5B358" strokeWidth="1.5" strokeDasharray="4 3" />
    <path d="M 156 106 L 160 112 L 164 106" fill="none" stroke="#C5B358" strokeWidth="1.5" />
    <text x="160" y="55" textAnchor="middle" fontSize="9" fontStyle="italic" fill="#C5B358">
      Easy step-over
    </text>
    <text x="160" y="170" textAnchor="middle" fontSize="9" fontStyle="italic" fill="#8B5A0F">
      Low arched top tube · straight drop to the pedals
    </text>
  </svg>
);

const WalkModeDiagram = () => (
  <svg viewBox="0 0 320 180" className="w-full h-40" role="img" aria-label="Diagram of walk mode assisting a rider up a hill">
    <defs>
      <marker id="ufa-arrow" markerWidth="10" markerHeight="10" refX="8" refY="5" orient="auto">
        <path d="M0,0 L8,5 L0,10 Z" fill="#C5B358" />
      </marker>
    </defs>
    {/* Hill slope */}
    <path d="M 10 155 L 310 55" stroke="#5A6B3A" strokeWidth="2" fill="none" strokeLinecap="round" />
    <path d="M 10 155 L 310 55 L 310 175 L 10 175 Z" fill="#5A6B3A" opacity="0.10" />
    {/* Walker figure */}
    <circle cx="105" cy="93" r="6" fill="currentColor" />
    <line x1="105" y1="99" x2="105" y2="123" stroke="currentColor" strokeWidth="3" />
    <line x1="105" y1="123" x2="97" y2="141" stroke="currentColor" strokeWidth="2" />
    <line x1="105" y1="123" x2="113" y2="141" stroke="currentColor" strokeWidth="2" />
    <line x1="105" y1="107" x2="132" y2="112" stroke="currentColor" strokeWidth="2" />
    {/* Bike beside walker */}
    <circle cx="152" cy="125" r="14" fill="none" stroke="currentColor" strokeWidth="2" />
    <circle cx="196" cy="117" r="14" fill="none" stroke="currentColor" strokeWidth="2" />
    <line x1="162" y1="118" x2="187" y2="112" stroke="currentColor" strokeWidth="2" />
    <line x1="162" y1="125" x2="173" y2="98" stroke="currentColor" strokeWidth="2" />
    <line x1="173" y1="98" x2="192" y2="103" stroke="currentColor" strokeWidth="2" />
    {/* Motor icon on rear hub */}
    <circle cx="152" cy="125" r="5" fill="#C5B358" />
    {/* Direction of assist */}
    <path d="M 220 100 L 275 82" stroke="#C5B358" strokeWidth="3" markerEnd="url(#ufa-arrow)" strokeLinecap="round" fill="none" />
    <text x="252" y="115" textAnchor="middle" fontSize="11" fontWeight="600" fill="#C5B358">
      4 kph
    </text>
    <text x="160" y="170" textAnchor="middle" fontSize="9" fontStyle="italic" fill="#8B5A0F">
      Motor pushes at walking pace while you push alongside
    </text>
  </svg>
);

const DualMotorDiagram = () => (
  <svg viewBox="0 0 320 180" className="w-full h-40" role="img" aria-label="Diagram of the Juliet 2 dual-motor drive, one motor at each wheel">
    {/* Ground line */}
    <line x1="10" y1="150" x2="310" y2="150" stroke="#5A6B3A" strokeWidth="1.5" strokeDasharray="4 3" />
    {/* Rear wheel */}
    <circle cx="80" cy="120" r="32" fill="none" stroke="currentColor" strokeWidth="2" />
    <circle cx="80" cy="120" r="14" fill="#C5B358" />
    <text x="80" y="125" textAnchor="middle" fontSize="14" fontWeight="700" fill="#0E3D14">M</text>
    <text x="80" y="76" textAnchor="middle" fontSize="9" fontStyle="italic" fill="#8B5A0F">Rear motor</text>
    {/* Front wheel */}
    <circle cx="240" cy="120" r="32" fill="none" stroke="currentColor" strokeWidth="2" />
    <circle cx="240" cy="120" r="14" fill="#C5B358" />
    <text x="240" y="125" textAnchor="middle" fontSize="14" fontWeight="700" fill="#0E3D14">M</text>
    <text x="240" y="76" textAnchor="middle" fontSize="9" fontStyle="italic" fill="#8B5A0F">Front motor</text>
    {/* Power arrows pointing at the ground */}
    <line x1="80" y1="152" x2="80" y2="140" stroke="#C5B358" strokeWidth="2" />
    <path d="M 76 148 L 80 154 L 84 148" fill="none" stroke="#C5B358" strokeWidth="2" />
    <line x1="240" y1="152" x2="240" y2="140" stroke="#C5B358" strokeWidth="2" />
    <path d="M 236 148 L 240 154 L 244 148" fill="none" stroke="#C5B358" strokeWidth="2" />
    {/* Frame between */}
    <line x1="112" y1="120" x2="208" y2="120" stroke="currentColor" strokeWidth="1.5" />
    {/* Label */}
    <text x="160" y="170" textAnchor="middle" fontSize="9" fontStyle="italic" fill="#8B5A0F">
      Two motors sharing the work · traction &amp; backup
    </text>
  </svg>
);

const FEATURES = [
  {
    id: 'step-through',
    title: 'Low step-through frame — mobility for everyone',
    lede: 'Swing on and off in full waterproofs, no leg-lifting over a top tube.',
    body:
      'The Juliet 2 drops its top tube almost to the wheel hub, so mounting and dismounting is a step-over rather than a leg-swing. Priceless if you\u2019re in bulky waterproofs, carrying panniers full of shopping, living with a prosthetic, hip replacement, reduced hip mobility, or simply prefer not to hoist a leg over a horizontal bar at the end of a long day. It also makes the bike safer to steady with both feet flat on the ground at a junction.',
    diagram: <StepThroughDiagram />,
  },
  {
    id: 'walk-mode',
    title: '4 kph walk mode — a helping hand on tricky ground',
    lede: 'A gentle motor push while you\u2019re on foot beside the bike.',
    body:
      'When the ground gets too steep, boggy, or you\u2019re lifting the bike over a stile, tap into walk mode. The motor takes over at a controlled 4 kph — the pace of a comfortable stroll — while you push the bike alongside. No heaving a fully-loaded bike up a soft embankment, no muscling it over a fallen tree, no exhausting yourself before you\u2019ve even ridden the down-side of the hill.',
    diagram: <WalkModeDiagram />,
  },
  {
    id: 'dual-motor',
    title: 'Dual-motor drive — the Highland hill-climber',
    lede: 'Two motors, one direction of travel: up.',
    body:
      'Most e-bikes have one motor. The Juliet 2 has two — one at each wheel — sharing the work. That means better traction on wet grass and loose gravel (both wheels pull, so one can\u2019t spin out on its own), and enough spare grunt to get you and a full pannier load up gradients that would stop a single-motor bike in its tracks. It also means if one motor were to fail on a remote track, you still ride home on the other.',
    diagram: <DualMotorDiagram />,
  },
];

export default function UserFriendlyFeatures() {
  const scrollToContact = () =>
    document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });

  return (
    <section
      id="user-friendly-features"
      data-testid="user-friendly-features"
      className="section bg-[var(--brand-parchment)] text-[var(--brand-forest)]"
    >
      <div className="max-w-5xl mx-auto px-6 lg:px-10">
        <span className="font-eyebrow">The Juliet 2 · in depth</span>
        <h2 className="mt-3 font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight">
          User-friendly <span className="italic text-[var(--brand-gold-soft)]">adaptive features</span>
        </h2>

        <p className="mt-6 max-w-3xl text-[var(--brand-muted)] text-lg leading-relaxed">
          The Juliet 2 is our flagship for a reason. Three practical features
          &mdash; a low step-through frame, a proper 4 kph walk mode, and true
          dual-motor drive &mdash; solve the real problems Highland riders bring
          us. Tap any row below to see how each feature works and who it&rsquo;s
          for.
        </p>

        {/* Brand vision quote — verbatim per the founder's brief. */}
        <blockquote
          data-testid="brand-vision-quote"
          className="mt-10 relative border-l-4 border-[var(--brand-gold)] bg-[var(--brand-forest)]/5 px-6 py-6 max-w-3xl"
        >
          <span
            aria-hidden="true"
            className="absolute -top-3 left-4 bg-[var(--brand-parchment)] px-2 text-xs uppercase tracking-[0.22em] text-[var(--brand-gold-soft)] font-semibold"
          >
            Our Vision
          </span>
          <p className="font-display italic text-xl leading-snug text-[var(--brand-forest-deep)]">
            &ldquo;Our team is committed to procuring the worldwide best tried
            and tested eco-friendly, user-friendly, fun and adventurous
            transport on the planet.&rdquo;
          </p>
          <footer className="mt-3 text-xs uppercase tracking-[0.22em] text-[var(--brand-muted)]">
            &mdash; EcoBike Camping Supplies Co.
          </footer>
        </blockquote>

        <div className="mt-12">
          <Accordion type="single" collapsible className="w-full">
            {FEATURES.map((f) => (
              <AccordionItem
                key={f.id}
                value={f.id}
                data-testid={`feature-accordion-${f.id}`}
                className="border-[var(--brand-olive)]/30"
              >
                <AccordionTrigger
                  data-testid={`feature-trigger-${f.id}`}
                  className="hover:no-underline py-6 group"
                >
                  <div className="flex flex-col items-start text-left gap-1 pr-4">
                    <span className="font-display text-lg sm:text-xl text-[var(--brand-forest)] group-hover:text-[var(--brand-gold-soft)] transition-colors">
                      {f.title}
                    </span>
                    <span className="text-sm font-normal italic text-[var(--brand-muted)]">
                      {f.lede}
                    </span>
                  </div>
                </AccordionTrigger>
                <AccordionContent
                  data-testid={`feature-content-${f.id}`}
                  className="pt-2 pb-8"
                >
                  <div className="grid md:grid-cols-[1fr_1.4fr] gap-6 md:gap-10 items-start">
                    <div className="bg-[var(--brand-forest)]/5 border border-[var(--brand-olive)]/25 p-5 text-[var(--brand-forest-deep)]">
                      {f.diagram}
                      <p className="mt-2 text-[10px] uppercase tracking-[0.22em] text-[var(--brand-muted)] text-center">
                        Diagram &mdash; indicative
                      </p>
                    </div>
                    <p className="text-base leading-relaxed text-[var(--brand-ink)]">
                      {f.body}
                    </p>
                  </div>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>

        <div className="mt-10">
          <p className="text-sm italic text-[var(--brand-muted)] max-w-2xl">
            Not sure which feature matters most for your rides?{' '}
            <button
              type="button"
              onClick={scrollToContact}
              data-testid="ufa-chat-link"
              className="link-expand font-semibold text-[var(--brand-forest)]"
            >
              Talk with our team
            </button>{' '}
            &mdash; five minutes on the phone saves an hour of guessing about
            which bike suits your local terrain and your specific needs.
          </p>
        </div>
      </div>
    </section>
  );
}
